import type { OrganizationProduct, ProductImportResult, ProductImportWarningRow } from '../types';
import {
  isGenericProductName,
  normalizeProductName,
  suggestNameFromDescription,
} from './productNaming';

export interface LiveImportWarning {
  warning: ProductImportWarningRow;
  productId: string;
}

const FIRST_DATA_ROW = 2;

const sameProductText = (left: OrganizationProduct, right: OrganizationProduct): boolean =>
  left.name === right.name &&
  (left.description ?? '') === (right.description ?? '') &&
  (left.priceRange ?? '') === (right.priceRange ?? '') &&
  (left.kind ?? null) === (right.kind ?? null) &&
  (left.aliases ?? []).join('\u0000') === (right.aliases ?? []).join('\u0000');

const locateCreated = (
  result: ProductImportResult,
  warning: ProductImportWarningRow,
): OrganizationProduct | null => {
  const rejectedBefore = result.rejectedRows.filter(
    (rejected) => rejected.row < warning.row,
  ).length;
  const byPosition = result.created[warning.row - FIRST_DATA_ROW - rejectedBefore];
  const wanted = normalizeProductName(warning.name);
  if (byPosition && normalizeProductName(byPosition.name) === wanted) return byPosition;
  return result.created.find((product) => normalizeProductName(product.name) === wanted) ?? null;
};

const resolveGenericName = (
  warning: ProductImportWarningRow,
  imported: OrganizationProduct,
  current: OrganizationProduct,
): ProductImportWarningRow | null => {
  if (!isGenericProductName(current.name)) return null;
  const descriptionUnchanged = (imported.description ?? '') === (current.description ?? '');
  const suggestedName =
    suggestNameFromDescription(current.name, current.description) ??
    (descriptionUnchanged ? warning.suggestedName : undefined);
  const { suggestedName: _previous, ...rest } = warning;
  return suggestedName ? { ...rest, suggestedName } : rest;
};

export const resolveLiveImportWarnings = (
  result: ProductImportResult,
  products: readonly OrganizationProduct[],
): LiveImportWarning[] =>
  (result.warnings ?? []).flatMap((warning) => {
    const imported = locateCreated(result, warning);
    if (!imported) return [];
    const current = products.find((product) => product.id === imported.id);
    if (!current) return [];

    if (warning.warning === 'generic_name') {
      const live = resolveGenericName(warning, imported, current);
      return live ? [{ warning: live, productId: current.id }] : [];
    }
    return sameProductText(imported, current) ? [{ warning, productId: current.id }] : [];
  });
